import { useEffect, useState } from "react";
import { Checkbox, Grid, GridColumn, GridRow, Header, Divider, Button } from "semantic-ui-react";
import styled from "styled-components";
import agents from "../../../app/api/agents";
import { EmailNotificationSettings as EmailNotificationSettingsModel } from "../../../app/models/notificationSetting";
import { useStore } from "../../../app/stores/store";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";

const StyledHeader = styled(Header)`margin-bottom: 20px !important;`;

const StyledContainer = styled.div`
        padding: ${props => props.theme.padding.featureContainer};
`;

const MainContainer = styled.div`
    display: flex;
    flex-direction: column;
    background-color: #fff;
    border-radius: 16px;
    width: auto;
    min-height: 100px;
    max-width: 700px;

    div.request-container {
        margin: 40px 40px 30px 40px;
    }
`;

const StyledRow = styled(GridRow)`
    padding-top: 5px !important;
    padding-bottom: 5px !important;
`;

const StyledColumn = styled(GridColumn)`
`;

const GridColumnHeader = styled(GridColumn)`
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 20px !important;
`;

const GridColumnTitle = styled(GridColumn)`
    font-weight: bold;
    font-size: 14px;
`;

const ActionButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 20px;
    padding-right: 50px;
`;

const StyledButton = styled.button`
    line-height: 24px;
    width: 100px;
    outline: none;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    color: #fff;
    padding: 8px 12px;
    background-color: #f8f9fa;
        color: #6a7e99;
    border: 1px solid #97a4b5;
    cursor: pointer;
`;

const StyledSaveButton = styled(StyledButton)`
    background-color: #1592ff;
    color: #fff;
    border: none;
    opacity: ${(props) => props.disabled ? '0.5' : '1'};
    ${(props) => props.disabled ? 'pointer-events: none;' : ''}
`;

const EmailNotificationSettings = () => {
    const history = useHistory();
    const { userStore } = useStore();
    const { user } = userStore;
    const [emailNotificationSettings, setEmailNotificationSettings] = useState<EmailNotificationSettingsModel | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!user) return;

        const fetchEmailNotificationSettings = async () => {
            const response = await agents.Notifications.getEmailNotificationSettings(user.id);
            console.log(response);
            setEmailNotificationSettings(response);
        };

        fetchEmailNotificationSettings();
    }, [user]);

    const handleSave = async () => {
        setIsSaving(true);

        if (!user || !emailNotificationSettings) return;

        await agents.Notifications.updateEmailNotificationSettings(emailNotificationSettings);
        toast.success('Email notification settings updated successfully');

        setIsSaving(false);
    }

    const handleCheckboxChange = (setting: keyof EmailNotificationSettingsModel) => {
        if (emailNotificationSettings) {
            setEmailNotificationSettings({ ...emailNotificationSettings, [setting]: !emailNotificationSettings?.[setting] });
        }
    };

    if (!emailNotificationSettings) return <LoadingComponent />;

    return <StyledContainer>
        <StyledHeader>Email Notification Settings</StyledHeader>
        <MainContainer>
            <div className="request-container">
                <h3>Request</h3>
                <Grid columns='equal'>
                    <StyledRow columns={9} >
                        <GridColumn width={4}></GridColumn>
                        <GridColumnHeader>Filed</GridColumnHeader>
                        <GridColumnHeader>Approved</GridColumnHeader>
                        <GridColumnHeader>Rejected</GridColumnHeader>
                        <GridColumnHeader>Canceled</GridColumnHeader>
                        <GridColumnHeader>Deleted</GridColumnHeader>
                    </StyledRow>
                    <StyledRow columns={9}>
                        <GridColumnTitle width={4}>Leave</GridColumnTitle>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.leaveFiled} onChange={(e) => handleCheckboxChange('leaveFiled')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.leaveApproved} onChange={(e) => handleCheckboxChange('leaveApproved')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.leaveRejected} onChange={(e) => handleCheckboxChange('leaveRejected')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.leaveCanceled} onChange={(e) => handleCheckboxChange('leaveCanceled')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.leaveDeleted} onChange={(e) => handleCheckboxChange('leaveDeleted')} /></StyledColumn>
                    </StyledRow>
                    <StyledRow columns={9}>
                        <GridColumnTitle width={4}>Overtime</GridColumnTitle>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.overtimeFiled} onChange={(e) => handleCheckboxChange('overtimeFiled')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.overtimeApproved} onChange={(e) => handleCheckboxChange('overtimeApproved')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.overtimeRejected} onChange={(e) => handleCheckboxChange('overtimeRejected')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.overtimeCanceled} onChange={(e) => handleCheckboxChange('overtimeCanceled')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.overtimeDeleted} onChange={(e) => handleCheckboxChange('overtimeDeleted')} /></StyledColumn>
                    </StyledRow>
                    <StyledRow columns={9}>
                        <GridColumnTitle width={4}>Offset Credit</GridColumnTitle>
                        <StyledColumn> <Checkbox toggle aria-label="Offset Credit Filed" checked={emailNotificationSettings?.offsetFiled} onChange={(e) => handleCheckboxChange('offsetFiled')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle aria-label="Offset Credit Approved" checked={emailNotificationSettings?.offsetApproved} onChange={(e) => handleCheckboxChange('offsetApproved')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle aria-label="Offset Credit Rejected" checked={emailNotificationSettings?.offsetRejected} onChange={(e) => handleCheckboxChange('offsetRejected')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle aria-label="Offset Credit Canceled" checked={false} disabled /></StyledColumn>
                        <StyledColumn> <Checkbox toggle aria-label="Offset Credit Deleted" checked={emailNotificationSettings?.offsetDeleted} onChange={(e) => handleCheckboxChange('offsetDeleted')} /></StyledColumn>
                    </StyledRow>
                    <StyledRow columns={9}>
                        <GridColumnTitle width={4}>Undertime</GridColumnTitle>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.undertimeFiled} onChange={(e) => handleCheckboxChange('undertimeFiled')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.undertimeApproved} onChange={(e) => handleCheckboxChange('undertimeApproved')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.undertimeRejected} onChange={(e) => handleCheckboxChange('undertimeRejected')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.undertimeCanceled} onChange={(e) => handleCheckboxChange('undertimeCanceled')} /></StyledColumn>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.undertimeDeleted} onChange={(e) => handleCheckboxChange('undertimeDeleted')} /></StyledColumn>
                    </StyledRow>
                    <StyledRow columns={9}>
                        <GridColumnTitle width={4}>Absent Notification</GridColumnTitle>
                        <StyledColumn> <Checkbox toggle checked={emailNotificationSettings?.absentNotification} onChange={(e) => handleCheckboxChange('absentNotification')} /></StyledColumn>
                    </StyledRow>
                </Grid>
            </div>
            <Divider />
            <ActionButtonContainer>
                <StyledSaveButton disabled={isSaving} onClick={handleSave}>Save</StyledSaveButton>
                <StyledButton onClick={() => history.push('/')}>Cancel</StyledButton>
            </ActionButtonContainer>
        </MainContainer>
    </StyledContainer>;
};

export default EmailNotificationSettings;